// Build the chat-handoff URL the CTAs on /pulses, /tasks, and /knowledge
// use to drop the user into a chat with a pre-filled message that
// auto-sends on arrival. The chat page (pages/index.tsx) consumes the
// query params on mount.
//
// - `new`: start a fresh thread (overrides any stale ?conversationId in the URL)
// - `conversationId`: continue an existing thread (used by Task chat-about)
// - `prefill` + `autosend=1`: the message to drop into the composer and send
//
// `as` is the URL the browser address bar shows (with the mount prefix —
// /orion-web or /symphony-web); `route` is the Next page route (no
// prefix, since the client router has no basePath). The caller passes
// `basePath` from useBasePath() so the handoff stays on whichever mount
// the user is on — without this, clicking a pulse template on
// /symphony-web teleported back to /orion-web.

export type ChatHandoffNew = { kind: 'new'; message: string };
export type ChatHandoffExisting = {
  kind: 'existing';
  conversationId: string;
  message: string;
  /**
   * The inbound outreach this message is a REPLY to. Rides to the chat as `?outreach=<id>` and is sent
   * as `references.outreachId`, which is what tells the agent to relay it with the outreach tools rather
   * than treating it as an ordinary request.
   *
   * `references` is a Struct on the wire, so this needs no proto change — the same way
   * `mentionedAgentId` rides (see `lib/agent-mentions.ts`).
   */
  outreachId?: string;
};
export type ChatHandoff = ChatHandoffNew | ChatHandoffExisting;

export function chatHandoffLink(
  handoff: ChatHandoff,
  basePath: string = '/orion-web',
): { route: string; path: string } {
  const params = new URLSearchParams();
  params.set('prefill', handoff.message);
  params.set('autosend', '1');
  if (handoff.kind === 'new') {
    params.set('new', '1');
    const qs = params.toString();
    return { route: `/?${qs}`, path: `${basePath}/?${qs}` };
  }
  // Existing conversation: the id rides as the `?conversation=<id>` query param
  // (never a path segment), so it doesn't change the route.
  params.set('conversation', handoff.conversationId);
  if (handoff.outreachId) params.set('outreach', handoff.outreachId);
  const qs = params.toString();
  return { route: `/?${qs}`, path: `${basePath}/?${qs}` };
}

// A repeated identical handoff inside this window is a double-click, not a new intent.
// Every `kind: 'new'` handoff resets the active thread and mints a fresh conversation on
// send, so rapid re-fires must collapse into one — this mirrors the native apps' 1.5s
// openNewChat seed latch (iOS AppRouter / Android AppRoot), same window on all three
// surfaces. A different message, a different target thread, or a deliberate re-click
// after the window all pass.
export const HANDOFF_LATCH_MS = 1500;

export type HandoffStamp = { message: string; conversationId: string | null; at: number };

export function isDuplicateHandoff(last: HandoffStamp | null, next: HandoffStamp): boolean {
  if (!last) return false;
  return (
    last.message === next.message &&
    last.conversationId === next.conversationId &&
    next.at - last.at < HANDOFF_LATCH_MS
  );
}

// Same handoff, but staying ON the current screen: the params land on `screenPath` instead of
// the home route, so the floating chat dock expands over the screen the user is looking at
// (use-chat-url opens it, ChatSurface autosends) and their context is never torn down. Use for
// CTAs on screens the user is actively working in (the Packs screens); the home-route variant
// above is for list rows whose whole point is to leave for the chat.
export function chatHandoffInPlace(
  handoff: ChatHandoff,
  basePath: string,
  screenPath: string,
): { route: string; path: string } {
  const params = new URLSearchParams();
  params.set('prefill', handoff.message);
  params.set('autosend', '1');
  if (handoff.kind === 'existing') {
    // Continue the thread the user is already in (the dock's open conversation) rather than
    // resetting to a fresh one — editing a pack picks up where its conversation left off.
    params.set('conversation', handoff.conversationId);
  } else {
    params.set('new', '1');
  }
  const qs = params.toString();
  return { route: `${screenPath}?${qs}`, path: `${basePath}${screenPath}?${qs}` };
}
